import MyContext from '@/shared/contexts/AppContext';
import { AttendanceSheetStatusEnum, getAttendanceSheetStatusEnumOptions } from '../../../enums/attendance-sheet-status.enum';
import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { IOpcao } from '@/shared/types/selectOption';
import { InternBondEnum } from '@/modules/business/attendance-sheet/enums/intern-bond.enum';
import { ListAllIntershipsOfTheMonthFilterDto } from '@/api.types.extra';
import { ListAllIntershipsOfTheMonthResponseDto, AttendanceSheetResponseDto } from '@/api.types';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { formatMonthParam, normalizeMonthParam } from '@/shared/utils/datas';
import { listAllIntershipsOfTheMonth, generateAttendanceSheetsForMonth, createAttendanceSheet } from '../../../attendance-sheet.service';
import { readEstagiarioForAttendanceSheetSelect, readEstagiarioForAttendanceSheetSelectWithFolha } from '@/modules/people/estagiario.service';
import { readPersonResponsibleSelect } from '@/modules/people/person.service';
import { toast } from 'react-toastify';
import { useDataTableStateStates } from '@/shared/components/tables/useDataTableState';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import { podeExibirFiltroResponsavel, ehPerfilColaborador, filtrarResponsaveisParaPessoa } from './perMonthFilters.utils';

export function useAttendanceSheets(tableState: useDataTableStateStates) {
  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  const { authVisao } = context;
  const { effectiveView, viewReady } = useNavigationView();
  const exibirFiltroResponsavel = useMemo(
    () => podeExibirFiltroResponsavel(authVisao, effectiveView),
    [authVisao, effectiveView],
  );
  const restringirResponsavelAoProprio = useMemo(
    () => ehPerfilColaborador(authVisao, effectiveView),
    [authVisao, effectiveView],
  );
  const pessoaIdSessao = context.state.pessoaId;

  const [searchParams, setSearchParams] = useSearchParams();
  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const permissaoCriarFolhas = ValidarPermissao(permissoes, [{ role: RoleEnum.FOLHA, level: PermissionEnum.CREATE }]);
  const permissaoExcluirFolhas = ValidarPermissao(permissoes, [{ role: RoleEnum.FOLHA, level: PermissionEnum.DELETE }]);

  const [loaded, setLoaded] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const [registros, setRegistros] = useState<[ListAllIntershipsOfTheMonthResponseDto[], number]>();
  const [filtrosStatus, setFiltrosStatus] = useState<IOpcao[]>([]);
  const [responsaveis, setResponsaveis] = useState<IOpcao[]>([]);
  const [estagiarios, setEstagiarios] = useState<IOpcao[]>([]);
  const [estagiariosComFolhaNoMes, setEstagiariosComFolhaNoMes] = useState<Set<number> | null>(null);
  const selectFolhaRequestSeqRef = useRef(0);

  const [comFolha, setComFolha] = useState(true);
  const [filtroResponsavel, setFiltroResponsavel] = useState<string>();
  const [filtroEstagiario, setFiltroEstagiario] = useState<number>();
  const [statusBusca, setStatusBusca] = useState<AttendanceSheetStatusEnum | number>(0);
  const [tipoVinculoBusca] = useState<InternBondEnum>(InternBondEnum.DEFENSORIA);
  const [unidadeBusca] = useState<number>(0);

  const [selectedItem, setSelectedItem] = useState<ListAllIntershipsOfTheMonthResponseDto>();
  const [folhaSelecionada, setFolhaSelecionada] = useState<ListAllIntershipsOfTheMonthResponseDto>();

  const [showModalCriarFolha, setShowModalCriarFolha] = useState(false);
  const [showModalCriarFolhas, setShowModalCriarFolhas] = useState(false);
  const [showModalNotificacaoIndividual, setShowModalNotificacaoIndividual] = useState(false);
  const [showModalNotificacoes, setShowModalNotificacoes] = useState(false);
  const [showModalResponsavel, setShowModalResponsavel] = useState(false);
  const [showModalAssinaturas, setShowModalAssinaturas] = useState(false);
  const [folhaParaAssinaturas, setFolhaParaAssinaturas] = useState<AttendanceSheetResponseDto | null>(null);

  const [mesBusca, setMesBusca] = useState<Date>(() => {
    const mesParam = searchParams.get('mes');
    if (mesParam) {
      const parsed = normalizeMonthParam(mesParam);
      if (parsed) return parsed;
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  useEffect(() => {
    if (isNaN(mesBusca.getTime())) return;
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('mes', formatMonthParam(mesBusca));

      statusBusca !== 0 ? newParams.set('status', statusBusca.toString()) : newParams.delete('status');
      filtroEstagiario ? newParams.set('estagiario', filtroEstagiario.toString()) : newParams.delete('estagiario');
      filtroResponsavel ? newParams.set('responsavel', filtroResponsavel) : newParams.delete('responsavel');
      newParams.set('comFolha', comFolha.toString());

      return newParams;
    });
  }, [mesBusca, statusBusca, filtroEstagiario, filtroResponsavel, comFolha, setSearchParams]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    if (params.status)
      setStatusBusca(parseInt(params.status));

    if (params.estagiario)
      setFiltroEstagiario(parseInt(params.estagiario));

    if (params.responsavel)
      setFiltroResponsavel(params.responsavel);

    if (params.comFolha)
      setComFolha(params.comFolha === 'true');
  }, [searchParams, estagiarios.length, responsaveis.length]);

  useEffect(() => {
    if (!restringirResponsavelAoProprio || !filtroResponsavel || !pessoaIdSessao) return;
    if (filtroResponsavel !== pessoaIdSessao) {
      setFiltroResponsavel(undefined);
    }
  }, [restringirResponsavelAoProprio, filtroResponsavel, pessoaIdSessao]);

  useEffect(() => {
    if (!exibirFiltroResponsavel) {
      setResponsaveis([]);
      setFiltroResponsavel(undefined);
    }
  }, [exibirFiltroResponsavel]);

  useEffect(() => {
    if (!viewReady && context.state.username) return;

    const carregarResponsaveis =
      exibirFiltroResponsavel && context.state.username
        ? readPersonResponsibleSelect()
        : Promise.resolve(null);

    Promise.all([
      readEstagiarioForAttendanceSheetSelect(),
      carregarResponsaveis,
    ])
      .then(response => {
        const estagiarios = response[0][0].map(e => ({ label: e.pessoa.nome, value: e.id } as IOpcao));
        estagiarios.unshift({ label: '-', value: 0 });
        setEstagiarios(estagiarios);

        if (response[1]) {
          const lista = response[1][0].map(e => ({
            label: e.nome,
            value: e.id
          } as unknown as IOpcao));
          const restrita = (restringirResponsavelAoProprio
            ? filtrarResponsaveisParaPessoa(lista, pessoaIdSessao)
            : lista) as unknown as IOpcao[];
          const responsaveis = [...restrita];
          responsaveis.unshift({ label: '-', value: 0 });
          setResponsaveis(responsaveis);
        } else if (!exibirFiltroResponsavel) {
          setResponsaveis([]);
        }
      })
      .catch(res => toast.error(String(res)))
      .finally(() => setLoaded(true));
  }, [viewReady, exibirFiltroResponsavel, restringirResponsavelAoProprio, pessoaIdSessao, context.state.username]);

  // Em acesso via token (sem username), o backend já sabe quem possui folha no mês.
  // Guardamos o conjunto de IDs para esconder somente os itens (sem afetar a visibilidade do seletor).
  useEffect(() => {
    if (context.state.username) {
      setEstagiariosComFolhaNoMes(null);
      return;
    }
    if (isNaN(mesBusca.getTime())) return;

    const ano = mesBusca.getFullYear();
    const mes = mesBusca.getMonth() + 1;
    const requestSeq = ++selectFolhaRequestSeqRef.current;
    readEstagiarioForAttendanceSheetSelectWithFolha(ano, mes)
      .then(([rows]) => {
        if (selectFolhaRequestSeqRef.current !== requestSeq) return; // resposta antiga (mês anterior)
        setEstagiariosComFolhaNoMes(new Set(rows.map((r) => r.id)));
      })
      .catch(() => {
        // se falhar, não esconde nada para não "sumir" opções
        if (selectFolhaRequestSeqRef.current !== requestSeq) return;
        setEstagiariosComFolhaNoMes(null);
      });
  }, [context.state.username, mesBusca]);

  // Aplica o "hidden" assim que a lista do backend chegar (independente do timing do handleBuscar).
  useEffect(() => {
    if (context.state.username) return;
    if (!estagiariosComFolhaNoMes) {
      // remove qualquer "hidden" aplicado anteriormente
      setEstagiarios((prev) => prev.map((opt) => ({ ...opt, hidden: false } as any)));
      return;
    }

    setEstagiarios((prev) =>
      prev.map((opt) => {
        if (Number(opt.value) === 0) return { ...opt, hidden: false } as any;
        const shouldShow = estagiariosComFolhaNoMes.has(Number(opt.value));
        return { ...opt, hidden: !shouldShow } as any;
      }),
    );

    if (filtroEstagiario && !estagiariosComFolhaNoMes.has(filtroEstagiario)) {
      setFiltroEstagiario(undefined);
    }
  }, [context.state.username, estagiariosComFolhaNoMes, filtroEstagiario]);

  const handleBuscar = () => {
    if (isNaN(mesBusca.getTime())) {
      toast.error('Mês inválido no filtro.');
      return;
    }
    const ano = mesBusca.getFullYear();
    const mes = mesBusca.getMonth() + 1;

    const dto: ListAllIntershipsOfTheMonthFilterDto = {
      unidade: unidadeBusca,
      status: statusBusca as number,
      vinculo: tipoVinculoBusca,
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      estagiarioId: filtroEstagiario,
      responsavelId: filtroResponsavel,
    };
    (dto as any).comFolha = comFolha;

    const dtoForSummary = { ...dto, skip: 0, take: 99999, status: 0, comFolha: context.state.username ? comFolha : true } as any;

    Promise.all([
      listAllIntershipsOfTheMonth(ano, mes, dto),
      listAllIntershipsOfTheMonth(ano, mes, dtoForSummary),
    ])
      .then(result => {
        const resultadoConsulta = result[0];
        const sumario = result[1];
        setRegistros(resultadoConsulta);

        const tStatus = getAttendanceSheetStatusEnumOptions().map(item => {
          const qtd = sumario[0].filter(a => a.folha?.status === item.value).length;
          return { value: Number(item.value), label: `${item.label} ${qtd > 0 ? `(${qtd})` : ''}` };
        });

        const qtdExcetoConcluidos = sumario[0].filter(a => a.folha?.status !== AttendanceSheetStatusEnum.CONCLUIDA).length;
        tStatus.unshift({ value: -1, label: `Exceto concluídos ${qtdExcetoConcluidos > 0 ? `(${qtdExcetoConcluidos})` : ''}` });
        setFiltrosStatus(tStatus);
      })
      .catch(res => toast.error(String(res)))
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (!loaded) return;
    handleBuscar();
  }, [unidadeBusca, mesBusca, statusBusca, tipoVinculoBusca, loaded, showModalCriarFolhas, comFolha, filtroEstagiario, filtroResponsavel]);

  const handleGenerateAttendanceSheetsForMonth = async () => {
    if (isNaN(mesBusca.getTime())) {
      toast.error('Mês inválido no filtro.');
      return;
    }
    setIsExecuting(true);
    generateAttendanceSheetsForMonth(mesBusca.getFullYear(), mesBusca.getMonth() + 1)
      .then(result => toast.success(`${result.length} folhas de frequência geradas.`))
      .catch(res => toast.error(String(res)))
      .finally(() => setIsExecuting(false));
  };

  const handleCriarFolha = () => {
    if (!selectedItem || selectedItem.folha?.id) return;
    setIsExecuting(true);
    createAttendanceSheet(selectedItem.estagio.id, selectedItem.ano, selectedItem.mes)
      .then(res => {
        toast.success(res ? 'Folha Criada' : String(res));
        setShowModalCriarFolha(false);
        handleBuscar();
      })
      .catch(res => toast.error(String(res)))
      .finally(() => setIsExecuting(false));
  };

  return {
    mesBusca, setMesBusca,
    statusBusca, setStatusBusca,
    filtroResponsavel, setFiltroResponsavel,
    filtroEstagiario, setFiltroEstagiario,
    filtrosStatus, registros, handleBuscar,
    comFolha, setComFolha,
    responsaveis, estagiarios,
    permissaoCriarFolhas, permissaoExcluirFolhas,
    isExecuting, handleCriarFolha, handleGenerateAttendanceSheetsForMonth,
    selectedItem, setSelectedItem,
    folhaSelecionada, setFolhaSelecionada,
    showModalCriarFolha, setShowModalCriarFolha,
    showModalCriarFolhas, setShowModalCriarFolhas,
    showModalNotificacaoIndividual, setShowModalNotificacaoIndividual,
    showModalNotificacoes, setShowModalNotificacoes,
    showModalResponsavel, setShowModalResponsavel,
    showModalAssinaturas, setShowModalAssinaturas,
    folhaParaAssinaturas, setFolhaParaAssinaturas,
    loaded,
    exibirFiltroResponsavel,
  };
}
